import axios from "../../helpers/bearerAxios";
import {logger} from "../loggingService";

const opportunityApiBaseUrl = import.meta.env.VITE_ONEBUTTON_OPPORTUNITY_BASE_URL;
const contractApiBaseUrl = import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL;

const getOppDetails = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/RetrieveOpportunityDetailsDirect?opportunityId=${opportunityId}`;
        const response = await axios.get<OpportunityDetailsResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type OpportunityDetailsResponse = {
    statusIndicators: StatusIndicatorsResponse;
    opportunityInfo: OpportunityInfoResponse;
};

export type StatusIndicatorsResponse = {
    botRunStatus: string | null;
    botRunDateTime: string | null;
    rsaStatus: string | null;
    botRunStatusflag: boolean;
    rsaStatusflag: boolean;
    eBillOverAllStatus: boolean | null;
    botActivityLog: string | null;
};

export type OpportunityInfoResponse = {
    sfdc: SfdcOpportunityResponse;
    eBill: EBillOpportunityResponse;
    siteCapture: SiteCaptureAssigneeResponse;
};

export type SfdcOpportunityResponse = {
    sfdcOpportunityId: string | null;
    sfdcAccountName: string | null;
    sfdcDirectLead: string | null;
    sfdcLeadGenerator: string | null;
    sfdcLeadGeneratorPrimeCloser: string | null;
    sfdcDLAssistedBy: string | null;
    sfdcOpportunityName: string | null;
    sfdcAddressOnTitle: string | null;
    sfdcType: string | null;
    sfdcTitle: string | null;
    sfdcPrimaryContact: string | null;
    sfdcPermittingAuthorityAHJTownship: string | null;
    sfdcAHJ: string | null;
    sfdcTrinitySalesPerson: string | null;
    sfdcAPN: string | null;
    sfdcLatitude: number | null;
    sfdcLongitude: number | null;
    sfdcDirectLeadId: string | null;
};

export type EBillOpportunityResponse = {
    eBillAddress: string | null;
};

export type SiteCaptureAssigneeResponse = {
    scAssignee: string | null;
};

const getFinancePartnerDetails = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/RetrieveFinancePartnerDetailsDirect?opportunityId=${opportunityId}`;
        const response = await axios.get<FinancePartnerDetailsResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type FinancePartnerDetailsResponse = {
    financePartnerType: string | null;
    financePartnerLeadId: string | null;
    financePartnerOpportunityName: string | null;
    financePartnerAddress: string | null;
    financePartnerTitleVerificationStatus: boolean | null;
    financePartnerCreditStatus: string | null;
    financePartnerCreditRunExpirationDate: string | null;
    financePartnerLeadPerson: string | null;
    financePartnerContact: FinancePartnerContactResponse[] | null;
    financePartnerSalesPersonOwner: string | null;
    financepartnerApprovedProducts: FinancePartnerApprovedProductResponse[] | null;
    financepartnerCurrentPlan: string | null;
    financepartnerEstAnnualUtilityIncrease: number | null;
};

export type FinancePartnerContactResponse = {
    index: string | null;
    value: string | null;
};

export type FinancePartnerApprovedProductResponse = {
    contract_Type: string | null;
    apr: number | null;
    state: string | null;
    term_Length: number | null;
    eligible: boolean;
};

const getApnDetails = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/GetAPNFromBlackKnight?opportunityId=${opportunityId}`;
        const response = await axios.get<ApnDetailsResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type ApnDetailsResponse = {
    siteXproAPN: string | null;
};

const getProductDetails = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/RetrieveProductInfo?opportunityId=${opportunityId}`;
        const response = await axios.get<ProductDetailsResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type ProductDetailsResponse = {
    sfdc: SfdcProductResponse;
};

export type SfdcProductResponse = {
    sfdcProductCategory: string | null;
    sfdcPrimaryFinancePartner: string | null;
    sfdcPurchaseMethod: string | null;
};

const getUtilityDetails = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/RetrieveUtilityInfo?opportunityId=${opportunityId}`;
        const response = await axios.get<UtilityDetailsResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type UtilityDetailsResponse = {
    sfdc: SfdcUtilityResponse;
    eBill: EBillUtilityResponse;
    score: UtilityScoreResponse;
    status: ValidateStatusResponse;
};

export type SfdcUtilityResponse = {
    utilityCompany: string | null;
    accountNumber: string | null;
    meterNumber: string | null;
    utilityUsage: string | null;
    lid: string | null;
    ppaRateEscalator: string | null;
    ppaRate: number | null;
};

export type EBillUtilityResponse = {
    utilityName: string | null;
    eBillAccountNumber: string | null;
    eBillMeterNumber: string | null;
    name: string | null;
    date: string | null;
    annualUsage: string | null;
    eBillLid: string | null;
};

export type UtilityScoreResponse = {
    utilityNameScore: number | null;
    accountNumberScore: number | null;
    meterNumberScore: number | null;
    dateScore: number | null;
    nameScore: number | null;
    annualUsageScore: number | null;
    lidScore: number | null;
};

export type ValidateStatusResponse = {
    utilityNameScoreStatus: boolean;
    accountNumberScoreStatus: boolean;
    meterNumberScoreStatus: boolean;
    nameScoreStatus: boolean;
    dateScoreStatus: boolean;
    annualUsageScoreStatus: boolean;
};

const getJobPhotos = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/GetJobPhotos?opportunityId=${opportunityId}`;
        const response = await axios.get<JobPhotosResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type JobPhotosResponse = {
    sfdc: SfdcBoxResponse;
    siteCapture: SiteCaptureResponse;
};

export type SfdcBoxResponse = {
    boxNoOfPhotos: number | null;
    boxNoOfEBill: string | null;
    photoCategories: PhotoCategoriesResponse[] | null;
}

export type PhotoCategoriesResponse = {
    category: string | null;
    count: number | null;
    confidentScore: number | null;
    scoreStatus: boolean | null;
}

export type SiteCaptureResponse = {
    siteCapNoOfPhotos: number | null;
}

const getNotes = async (opportunityId: string) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/RetrieveSalesForceNotes?opportunityId=${opportunityId}`;
        const response = await axios.get<NotesResponse>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type NotesResponse = {
    sfdcNotes: SfdcNotesResponse | null;
};

export type SfdcNotesResponse = {
    salesQualificationNotes: string | null;
    salesDirectLeadCustomerNotes: string | null;
    salesPersonNotes: string | null;
    creditFinanceNotes: string | null;
};

const saveValidationStatus = async (request: SaveValidationStatusRequest) => {
    try {
        const url = `${opportunityApiBaseUrl}/api/Opportunity/SaveValidationStatus`;
        const response = await axios.post(url, request);
        return response.status === 200;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type SaveValidationStatusRequest = {
    opportunityId: number;
    source: string;
    field: string;
    status: boolean;
}

const getSystemDesignDetails = async (opportunityId: string) => {
    try {
        const url = `${contractApiBaseUrl}/api/Contract/GetSystemDetailsDirect?opportunityId=${opportunityId}`;
        const response = await axios.get<SystemDesignDetailsDirect[]>(url);
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

export type SystemDesignDetailsDirect = {
    EquipmentDetails: EquipmentDetailsDirect[] | null;
    Source: string | null;
    SystemSize: string | null;
    Production: string | null;
    YearlySaving: string | null;
    EPC: string | null;
    FinacePartner: string | null;
    ModuleManufacturer: string | null;
    ModuleModel: string | null;
    ModuleQuantity: number | null;
    InverterManufacturer: string | null;
    InverterModel: string | null;
    InverterQuantity: string | null;
    OptimizerManufacturer: string | null;
    OptimizerModel: string | null;
    OptimizerQuantity: string | null;
    RackingManufacturer: string | null;
    RackingModel: string | null;
    RackingType: string | null;
    RackingParts: string | null;
}

export type EquipmentDetailsDirect = {
    ArrayNumber: string;
    ModuleWattage: string | null;
    MonitorManufacturer: string | null;
    MonitorModel: string | null;
    Tilt: string | null;
    Azimuth: string | null;
    SolarAvailability: string | null;
    BuildingType: string | null;
    ModuleQuantity: string | null;
}

/** Used when a non-critical validation API fails so the modal still renders. */
export function getDefaultFinancePartnerDetails(): FinancePartnerDetailsResponse {
    return {
        financePartnerType: 'Finance Partner',
        financePartnerLeadId: null,
        financePartnerOpportunityName: null,
        financePartnerAddress: null,
        financePartnerTitleVerificationStatus: null,
        financePartnerCreditStatus: null,
        financePartnerCreditRunExpirationDate: null,
        financePartnerLeadPerson: null,
        financePartnerContact: null,
        financePartnerSalesPersonOwner: null,
        financepartnerApprovedProducts: null,
        financepartnerCurrentPlan: null,
        financepartnerEstAnnualUtilityIncrease: null,
    };
}

export function getDefaultApnDetails(): ApnDetailsResponse {
    return { siteXproAPN: null };
}

export function getDefaultProductDetails(): ProductDetailsResponse {
    return {
        sfdc: {
            sfdcProductCategory: null,
            sfdcPrimaryFinancePartner: null,
            sfdcPurchaseMethod: null,
        },
    };
}

export function getDefaultUtilityDetails(): UtilityDetailsResponse {
    return {
        sfdc: {
            utilityCompany: null,
            accountNumber: null,
            meterNumber: null,
            utilityUsage: null,
            lid: null,
            ppaRateEscalator: null,
            ppaRate: null,
        },
        eBill: {
            utilityName: null,
            eBillAccountNumber: null,
            eBillMeterNumber: null,
            name: null,
            date: null,
            annualUsage: null,
            eBillLid: null,
        },
        score: {
            utilityNameScore: null,
            accountNumberScore: null,
            meterNumberScore: null,
            dateScore: null,
            nameScore: null,
            annualUsageScore: null,
            lidScore: null,
        },
        status: {
            utilityNameScoreStatus: false,
            accountNumberScoreStatus: false,
            meterNumberScoreStatus: false,
            nameScoreStatus: false,
            dateScoreStatus: false,
            annualUsageScoreStatus: false,
        },
    };
}

export function getDefaultJobPhotos(): JobPhotosResponse {
    return {
        sfdc: { boxNoOfPhotos: null, boxNoOfEBill: null, photoCategories: null },
        siteCapture: { siteCapNoOfPhotos: null },
    };
}

export function getDefaultNotes(): NotesResponse {
    return { sfdcNotes: null };
}

const opportunityValidationService = {
    getOppDetails,
    getFinancePartnerDetails,
    getApnDetails,
    getProductDetails,
    getUtilityDetails,
    getJobPhotos,
    getNotes,
    saveValidationStatus,
    getSystemDesignDetails,
};

export default opportunityValidationService;